import { z } from 'zod'

import type { Logger } from '@/observability/logger/types'

/**
 * The closed vocabularies of text facts: a fact whose value is one of a fixed set of tokens
 * holds that set here, as the enum its schema reads, so the content standard can name the
 * branches a `select` over it may take. Each warehouse vocabulary is declared once in the
 * serving contract (`dbt/models/serving/_serving.yml`), which builds fail on, and the enum
 * here is held equal to it by `vocabularies.test.ts`; a token is a code, and its wording is
 * copy (→ invariants.md#facts-canonical-store-editorial).
 */

/** A route's or a guide's mode. */
export const TransModeSchema = z.enum(['bus', 'train', 'ferry'])
export type TransMode = z.infer<typeof TransModeSchema>

/** A mode with a hub page: the route modes, and flight, which has a hub but no routes. */
export const HubModeSchema = z.enum(['bus', 'train', 'ferry', 'flight'])

/** Which place a place hub is for: a country, or a region (state, province, county …) of one. */
export const PlaceHubKindSchema = z.enum(['country', 'region'])

/** The one vehicle combination a route's trips take other than its own mode; `other` when
 *  they take several. */
export const MultimodalTypeSchema = z.enum([
    'bus',
    'train',
    'ferry',
    'bus_train',
    'train_bus',
    'other',
])

/** Whether none, some or all of a route's trips combine vehicles other than its own mode. */
export const MultimodalCompositionSchema = z.enum([
    'NO_MULTIMODAL_TRIPS',
    'SOME_MULTIMODAL_TRIPS',
    'ALL_MULTIMODAL_TRIPS',
])

/** A route's fare band for its mode. */
export const PriceClassSchema = z.enum(['low', 'cheap', 'medium', 'expensive', 'high'])

/** A route's distance band. */
export const DistanceClassSchema = z.enum(['short', 'medium', 'long', 'trek'])

/** A feature the carrier data lists for a carrier. */
export const CarrierFeatureSchema = z.enum([
    'air_conditioning',
    'attendant',
    'bathroom',
    'beverage',
    'bikes_allowable',
    'extra_legroom',
    'free_water',
    'games',
    'green_cert',
    'loyalty_reward',
    'luxury',
    'meal',
    'movie',
    'newspaper_magazine',
    'pets_allowable',
    'power_outlet',
    'qr_code',
    'satellite_tv',
    'snacks',
    'usb_power',
    'wifi',
])
export type CarrierFeature = z.infer<typeof CarrierFeatureSchema>

/** A way the carrier data says a carrier's ticket is delivered or checked. `eticket` is the
 *  carrier data's own spelling for some carriers, the same delivery as `eTicket`. */
export const TicketDeliverySchema = z.enum([
    'eTicket',
    'eticket',
    'exactCCRequiredForWillCall',
    'idRequired',
    'mobilePass',
    'passengerList',
    'print',
    'ticketNumber',
    'willCall',
])

/** The review form's travel-type answer code, as it prints. */
export const TravelerTypeSchema = z.enum(['1', '2', '3', '4', '5', '6', '7'])

/** A yes/no the warehouse classifies a route by, as the word a `select` branches on, indexed
 *  by the boolean's number so the word a boolean reads as and the enum are one list. */
export const FLAG_VALUES = ['false', 'true'] as const
export const FlagSchema = z.enum(FLAG_VALUES)
export type Flag = z.infer<typeof FlagSchema>

/** Where a token was read, as `model.column` or `model.column:path`, and the token. */
export interface OutsideVocabulary {
    readonly source: string
    readonly token: string
}
export type ReportOutsideVocabulary = (outside: OutsideVocabulary) => void

/**
 * A fact's token, or absent. A token outside the vocabulary is read as absent and reported:
 * the contract fails the warehouse build on it, so it reaches a page only while a deploy
 * that widens a vocabulary is part-way through, and a page reading the fact as absent
 * renders its `none` or `other` branch rather than failing.
 */
export function readVocabulary<E extends z.ZodEnum>(
    vocabulary: E,
    value: unknown,
    source: string,
    report: ReportOutsideVocabulary,
): z.infer<E> | undefined {
    if (value === null || value === undefined) {
        return undefined
    }
    const token =
        typeof value === 'string' || typeof value === 'number'
            ? String(value)
            : JSON.stringify(value)
    const parsed = vocabulary.safeParse(token)
    if (parsed.success) {
        return parsed.data
    }
    report({ source, token })
    return undefined
}

/** For a caller with nowhere to report to, such as a diagnostic comparing renders. */
export const ignoreOutsideVocabulary: ReportOutsideVocabulary = () => undefined

/** Reports a token outside its vocabulary as a warning through a logger. */
export const warnOutsideVocabulary =
    (logger: Pick<Logger, 'withMetadata'>): ReportOutsideVocabulary =>
    (outside) =>
        logger.withMetadata({ ...outside }).warn('facts value outside its vocabulary')
